import type React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { SHIRES } from '../components/shared/electorate-shires';
import { ResponsibilitiesExplainer } from '../components/shared/ResponsibilitiesExplainer';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';


export const Route = createFileRoute('/about')({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: 'About — Warren-Blackwood Electorate' },
      {
        name: 'description',
        content:
          'About the Warren-Blackwood electorate: six south-west WA shires from the Margaret River coast to Denmark, with notes on the data behind this interactive map.',
      },
      { property: 'og:title', content: 'About — Warren-Blackwood Electorate' },
      {
        property: 'og:description',
        content:
          'About the Warren-Blackwood electorate and the data behind this interactive map.',
      },
    ],
  }),
});

function AboutPage() {
  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
        <Breadcrumbs items={[{ label: 'Map', to: '/' }, { label: 'About' }]} />

        <h1
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            color: 'var(--green)',
            marginBottom: 'var(--sp-sm)',
          }}
        >
          About this map
        </h1>
        <p style={{ color: 'var(--ink-mid)', fontStyle: 'italic', marginBottom: 'var(--sp-lg)' }}>
          A small, hand-built guide to one of Western Australia’s most varied state electorates.
        </p>

        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
            The electorate
          </h2>
          <p style={{ marginBottom: '0.75rem' }}>
            Warren-Blackwood is a Legislative Assembly electorate in the south-west corner of
            Western Australia. It runs from the Indian Ocean at Margaret River, across the karri
            and jarrah forests of the Blackwood Valley, to the Southern Ocean at Denmark — taking
            in six local government areas along the way.
          </p>
          <p>
            The shires share a forested, agricultural, lightly-populated character but each has
            its own distinct economy: wine and tourism on the coast, timber and horticulture in
            the middle, mining in Greenbushes, country music in Boyup Brook.
          </p>
        </section>

        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
            The six shires
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: '0.5rem' }}>
            {SHIRES.map((s) => (
              <li key={s.id}>
                <Link
                  to="/shire/$name"
                  params={{ name: s.id }}
                  style={{
                    color: 'var(--green)',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                  }}
                >
                  {s.name}
                </Link>
                <span style={{ color: 'var(--ink-mid)' }}> — {s.blurb}</span>
              </li>
            ))}
          </ul>
        </section>

        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
            Regional collaboration
          </h2>
          <p style={{ marginBottom: '0.75rem' }}>
            The{' '}
            <a
              href="https://www.wbac.net.au/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green)', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              Warren Blackwood Alliance of Councils
            </a>{' '}
            (WBAC) is a voluntary regional body formed in 2001 after the timber-industry restructure. It brings together the shires of the Warren and Blackwood catchments to work collectively on issues that cross council boundaries — bushfire recovery, regional growth planning, and climate resilience.
          </p>
          <p>
            Four of the six Warren-Blackwood shires (Boyup Brook, Bridgetown-Greenbushes, Manjimup and Nannup) are WBAC members. The Alliance is one of the longest-standing regional local-government collaborations in Western Australia.
          </p>
        </section>

        <ResponsibilitiesExplainer variant="panel" />

        <section style={{ marginBottom: 'var(--sp-lg)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
            Elections and enrolment
          </h2>
          <p style={{ marginBottom: '0.75rem' }}>
            The next Western Australian state election is expected in March 2029.{' '}
            Warren-Blackwood is a lower house (Legislative Assembly) electorate, so its
            representative sits in the chamber that forms government.
          </p>
          <p>
            <a
              href="https://www.elections.wa.gov.au"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--green)', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              Check or update your enrolment at the WA Electoral Commission
            </a>
            .
          </p>
        </section>

        <section id="data-sources" style={{ marginBottom: 'var(--sp-lg)' }}>
          <h2 style={{ fontSize: '1.4rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
            Data sources
          </h2>
          <p style={{ marginBottom: '0.75rem' }}>
            Every figure on this site is traceable to a public source. The list below is the
            complete reference set — individual shire and profile pages cite the specific
            dataset in their own <em>Sources</em> section.
          </p>

          <h3 style={subHeading}>Geography and boundaries</h3>
          <ul style={listStyle}>
            <li>
              State electorate boundaries (2023 redistribution) —{' '}
              <SrcLink href="https://www.elections.wa.gov.au/electorates">
                WA Electoral Commission, State Electorates
              </SrcLink>
              .
            </li>
            <li>
              Local Government Area boundaries —{' '}
              <SrcLink href="https://www.abs.gov.au/statistics/standards/australian-statistical-geography-standard-asgs-edition-3/jul2021-jun2026/non-abs-structures/local-government-areas">
                ABS ASGS Edition 3 (Jul 2021 – Jun 2026), Local Government Areas
              </SrcLink>
              .
            </li>
            <li>
              Suburbs and localities (SAL) —{' '}
              <SrcLink href="https://www.abs.gov.au/statistics/standards/australian-statistical-geography-standard-asgs-edition-3/jul2021-jun2026/non-abs-structures/suburbs-and-localities">
                ABS ASGS Edition 3, Suburbs and Localities
              </SrcLink>
              .
            </li>
            <li>
              Cadastral and town reference data —{' '}
              <SrcLink href="https://www.wa.gov.au/organisation/department-of-planning-lands-and-heritage">
                WA Department of Planning, Lands and Heritage
              </SrcLink>{' '}
              (Landgate Locate / SLIP open datasets). Boundaries are simplified and reprojected
              for this map; they are not authoritative for legal or planning purposes.
            </li>
          </ul>

          <h3 style={subHeading}>Population, area and demographics</h3>
          <ul style={listStyle}>
            <li>
              Per-shire population, age, income, housing tenure, ancestry, country of birth and
              industries of employment —{' '}
              <SrcLink href="https://www.abs.gov.au/census/find-census-data">
                ABS 2021 Census of Population and Housing, All persons QuickStats
              </SrcLink>{' '}
              (per LGA). Each shire page links directly to its specific QuickStats page
              (LGA50280 Augusta-Margaret River, LGA50770 Boyup Brook, LGA50840
              Bridgetown-Greenbushes, LGA52730 Denmark, LGA55180 Manjimup, LGA56300 Nannup).
            </li>
            <li>
              Shire land area — ABS 2021 QuickStats (rounded to the nearest 10 km²).
            </li>
            <li>
              Estimated Resident Population (ERP) time series — electorate to 2025,
              shires to 2024 —{' '}
              <SrcLink href="https://www.abs.gov.au/statistics/people/population/regional-population/latest-release">
                ABS Regional Population (cat. 3218.0)
              </SrcLink>
              .
            </li>
          </ul>

          <h3 style={subHeading}>Warren-Blackwood community profile</h3>
          <ul style={listStyle}>
            <li>
              Electorate-wide demographic chapters (age, ancestry, birthplace, language,
              religion, qualifications, employment, income, housing, households) —{' '}
              <SrcLink href="https://profile.id.com.au/wa/state-electorate?WebID=145">
                .id (informed decisions) Warren-Blackwood community profile
              </SrcLink>
              , prepared for the WA Parliamentary Library. Underlying data is ABS 2016 and
              2021 Census with 2025 ERP overlays.
            </li>
            <li>
              Headline indicators and benchmark comparisons against Greater Perth, WA and
              Australia are taken from the same .id profile PDFs.
            </li>
          </ul>

          <h3 style={subHeading}>Council contacts and services</h3>
          <ul style={listStyle}>
            <li>
              Phone, email, postal address, meeting schedules, rates / planning / waste /
              reporting links, and local emergency pages — sourced directly from each shire's
              own website:{' '}
              <SrcLink href="https://www.amrshire.wa.gov.au">amrshire.wa.gov.au</SrcLink>,{' '}
              <SrcLink href="https://www.boyupbrook.wa.gov.au">boyupbrook.wa.gov.au</SrcLink>,{' '}
              <SrcLink href="https://www.bridgetown.wa.gov.au">bridgetown.wa.gov.au</SrcLink>,{' '}
              <SrcLink href="https://www.denmark.wa.gov.au">denmark.wa.gov.au</SrcLink>,{' '}
              <SrcLink href="https://www.manjimup.wa.gov.au">manjimup.wa.gov.au</SrcLink>,{' '}
              <SrcLink href="https://www.nannup.wa.gov.au">nannup.wa.gov.au</SrcLink>.
            </li>
            <li>
              Regional collaboration —{' '}
              <SrcLink href="https://www.wbac.net.au/">
                Warren Blackwood Alliance of Councils (WBAC)
              </SrcLink>
              .
            </li>
          </ul>

          <h3 style={subHeading}>Emergency and warnings</h3>
          <ul style={listStyle}>
            <li>
              Fire and emergency warnings —{' '}
              <SrcLink href="https://www.emergency.wa.gov.au">
                Department of Fire and Emergency Services (DFES), Emergency WA
              </SrcLink>
              .
            </li>
            <li>
              State Emergency Service — DFES SES (132 500). Life-threatening emergencies —
              Triple Zero (000).
            </li>
          </ul>

          <h3 style={subHeading}>Government and parliamentary context</h3>
          <ul style={listStyle}>
            <li>
              Local government powers and responsibilities —{' '}
              <SrcLink href="https://www.legislation.wa.gov.au/legislation/statutes.nsf/main_mrtitle_551_homepage.html">
                Local Government Act 1995 (WA)
              </SrcLink>
              .
            </li>
            <li>
              Member for Warren-Blackwood and Legislative Assembly business —{' '}
              <SrcLink href="https://www.parliament.wa.gov.au/Parliament/MembList.nsf/WCurrentMembAssem?openview">
                Parliament of Western Australia, Legislative Assembly members
              </SrcLink>
              .
            </li>
          </ul>

          <h3 style={subHeading}>About this build</h3>
          <p style={{ marginBottom: '0.75rem' }}>
            The map is hand-drawn from the boundary datasets above and projected to fit a
            mobile-first canvas. The state of the map (which shire is open, which locality is
            selected) is encoded in the URL query string, so any view can be bookmarked or
            shared. No analytics, tracking pixels, or third-party scripts run on these pages.
          </p>
          <p style={{ color: 'var(--ink-mid)', fontSize: '0.9rem' }}>
            Spotted an error or a missing source? Please raise it — this site is a small
            civic resource and accuracy matters more than completeness.
          </p>
      </section>
    </main>
  );
}


function SrcLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--green)', textDecoration: 'underline', textUnderlineOffset: 3 }}
    >
      {children}
    </a>
  );
}

const subHeading: React.CSSProperties = {
  fontSize: '1.05rem',
  color: 'var(--green)',
  fontFamily: 'var(--font-heading)',
  margin: '1rem 0 0.4rem',
};

const listStyle: React.CSSProperties = {
  margin: '0 0 0.75rem 1.1rem',
  padding: 0,
  display: 'grid',
  gap: '0.4rem',
};

